import React, { useEffect, useRef, useState } from 'react'
import { createGuid, showToast } from '../../../utils';

interface Folder {
    id: string;
    name: string;
    photos: Array<any>;
    isExistingFolder: boolean;
}

interface ILocationGalleries {
    onCreateFolder: () => void;
    folders: Array<any>;
    setActiveFolder: (value: any) => void;
    onImportFolder: (data: any) => void;
    onDeleteFolder: (folderId: string) => void;
}

const HIDDEN_SYSTEM_FILES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);

const isHiddenOrSystemFile = (name?: string): boolean => {
    if (!name) return false;
    const lowerName = name.toLowerCase();
    return lowerName.startsWith('.') || HIDDEN_SYSTEM_FILES.has(lowerName);
};

const LocationGalleries = ({ onCreateFolder, onImportFolder, folders, setActiveFolder, onDeleteFolder }: ILocationGalleries) => {

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isDragging, setIsDragging] = useState(false);
    const [hoveredFolderId, setHoveredFolderId] = useState<string | null>(null);

    const handleFolder = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);

        const groupedFolders: Record<string, Folder> = {};

        files.forEach((file) => {
            if (isHiddenOrSystemFile(file?.name)) {
                return;
            }

            const folderName = file.webkitRelativePath.split("/")[0];

            if (!groupedFolders[folderName]) {
                groupedFolders[folderName] = {
                    id: createGuid(),
                    name: folderName,
                    photos: [],
                    isExistingFolder: false,
                };
            }

            groupedFolders[folderName]?.photos?.push({
                id: "",
                name: file?.name || 'Test',
                image: URL.createObjectURL(file),
                file: file,
                thumbnail: false,
                type: file?.type || 'image'
            });
        });

        const result: Folder[] = Object.values(groupedFolders);
        const existingFolderNames = new Set(folders?.map((folder: Folder) => folder?.name?.toLowerCase())
        );

        const duplicateFolders = result?.filter((folder) => existingFolderNames?.has(folder?.name?.toLowerCase()));

        if (duplicateFolders?.length > 0) {
            showToast(`Folder already imported: ${duplicateFolders?.map((folder) => folder?.name)?.join(", ")}`, 'error');
            return;
        }

        onImportFolder(result);
    };

    const readDirectory = async (directoryEntry: any, folder: Folder): Promise<void> => {
        const reader = directoryEntry?.createReader();

        const readEntries = () =>
            new Promise<any[]>((resolve) => reader?.readEntries(resolve));

        let entries: any[] = [];

        while (true) {
            const batch = await readEntries();

            if (!batch.length) break;

            entries = [...entries, ...batch];
        }

        for (const entry of entries) {
            if (isHiddenOrSystemFile(entry?.name)) {
                continue;
            }

            if (entry?.isFile) {
                await new Promise<void>((resolve) => {
                    entry?.file((file: File) => {
                        folder?.photos?.push({
                            id: "",
                            name: file?.name,
                            image: URL.createObjectURL(file),
                            file,
                            thumbnail: false
                        });
                        resolve();
                    });
                });
            } else if (entry?.isDirectory) {
                await readDirectory(entry, folder);
            }
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);

        const items = Array.from(e.dataTransfer?.items);

        if (!items?.length) return;

        // IMPORTANT: webkitGetAsEntry() must be called synchronously for every item
        // before any `await`, otherwise the DataTransferItemList gets invalidated
        // by the browser and subsequent items (2nd, 3rd, ...) return null.
        const entries = items?.map((item) => (item as any)?.webkitGetAsEntry?.());

        if (entries?.some((entry) => !entry || !entry?.isDirectory)) {
            showToast("Please drop folders only.", "error");
            return;
        }

        const result: Folder[] = [];

        for (const entry of entries) {
            const folder: Folder = {
                id: createGuid(),
                name: entry?.name || '',
                photos: [],
                isExistingFolder: false,
            };

            await readDirectory(entry, folder);

            result.push(folder);
        }

        const existingFolderNames = new Set(folders?.map((folder: Folder) => folder?.name?.toLowerCase())
        );

        const duplicateFolders = result?.filter((folder) => existingFolderNames?.has(folder?.name?.toLowerCase()));

        if (duplicateFolders.length > 0) {
            showToast(`Folder already imported: ${duplicateFolders?.map((folder) => folder?.name)?.join(", ")}`, 'error');
            return;
        }

        onImportFolder(result);
    };

    const handleDeleteFolder = (e: React.MouseEvent, folderId: string) => {
        e.stopPropagation();
        onDeleteFolder(folderId);
    };

    useEffect(() => {
        fileInputRef.current?.setAttribute("webkitdirectory", "");
    }, []);

    return (
        <div className="cl-step">
            <h3 className="cl-step__heading">Add Photos</h3>
            <p className="cl-photos__sub">Galleries</p>
            <div className="cl-galleries">
                {folders?.length > 0 && folders?.map((folder, idx) => (
                    <div
                        key={`${idx}_${folder?.id}`}
                        className="cl-gallery-item-wrapper"
                        style={{ position: "relative", display: "inline-block" }}
                        onMouseEnter={() => setHoveredFolderId(folder?.id)}
                        onMouseLeave={() => setHoveredFolderId(null)}
                    >
                        <div
                            className="cl-gallery-item"
                            style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <button
                                onClick={() => setActiveFolder(folder)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    flex: 1,
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    textAlign: "left",
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="#f6c90e">
                                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                                </svg>

                                {folder?.name}
                            </button>

                            {!folder?.isExistingFolder &&
                                <button
                                    className="cl-gallery-item__delete"
                                    onClick={(e) => handleDeleteFolder(e, folder?.id)}
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "4px",
                                    }}
                                    aria-label="Delete folder"
                                >
                                    <img src='/assets/images/delete-btn-red.svg' />
                                </button>
                            }
                        </div>
                    </div>
                ))}
                <button
                    className="cl-gallery-item cl-gallery-item--new"
                    onClick={onCreateFolder}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b5ce7" strokeWidth="2">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    New folder
                </button>

                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{ display: "inline-block" }}
                >
                    <button
                        className={`cl-gallery-item cl-gallery-item--new ${isDragging ? "drag-active" : ""}`}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b5ce7" strokeWidth="2">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                        {isDragging ? "Drop folder here" : "Import Folder"}
                    </button>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFolder}
                    hidden
                />
            </div>
        </div>
    )
}

export default LocationGalleries